
import Vue from 'vue';
import Component from 'vue-class-component';
import {Action, Getter} from 'vuex-class';

interface ServiceStatus {
  text: string;
  icon: string;
  color: string;
  textColor: string;
  description: string;
}

@Component({})
export default class ServiceStatusCard extends Vue {
  @Getter isElectron!: boolean;
  @Getter isDevMode!: boolean;

  @Action checkSignService!: () => Promise<boolean>;
  @Action startSignService!: () => Promise<void>;
  @Action stopSignService!: () => Promise<void>;
  @Action startProxyService!: () => Promise<void>;
  @Action stopProxyService!: () => Promise<void>;
  @Action availableCards!: (payload?: { silent?: boolean }) => Promise<void>;
  @Action checkUyapSession!: () => Promise<void>;

  signRunning = false;
  signBusy = true;
  smartCardLoading = false;
  autoRefreshEnabled = true;
  refreshIntervalSec = 15;
  nextRefreshIn = 15;

  private countdownTimer: number | null = null;

  mounted() {
    this.heartbeat();
    this.restorePreferences();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.handleVisibilityChange as any);
    }
    if (this.autoRefreshEnabled) {
      this.startAutoRefreshTimers();
    }

    if (this.isElectron) {
      this.refreshSmartCards().catch(() => undefined);
    }

  }

  beforeDestroy() {
    this.stopAutoRefreshTimers();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange as any);
    }
  }

  get serviceStatus(): ServiceStatus {
    if (this.signRunning) {
      return {
        text: 'Açık',
        icon: 'mdi-check-circle',
        color: '#4caf50',
        textColor: 'white',
        description: 'İmza servisi hazır durumda ve görevleri almaya devam ediyor.',
      };
    }

    return {
      text: 'Kapalı',
      icon: 'mdi-alert-circle',
      color: '#ef5350',
      textColor: 'white',
      description: 'Servis şu an kapalı görünüyor. Yukarıdan başlatıp tekrar deneyebilirsiniz.',
    };
  }

  async onStartClick() {
    this.signBusy = true;
    try {
      await this.startSignService();
      await this.startProxyService();
      await this.heartbeat();
      if (this.isDevMode) {
        this.$nextTick(() => {
          this.checkUyapSession();
        });
      }
    } finally {
      this.signBusy = false;
    }
  }

  async onStopClick() {
    this.signBusy = true;
    try {
      await this.stopSignService();
      await this.stopProxyService();
      await this.heartbeat();
    } finally {
      this.signBusy = false;
    }
  }

  async onRestartClick() {
    await this.onStopClick();
    await this.onStartClick();
  }

  openNewEicrapro() {
    this.$http.get('https://imza.eicrapro.com:6090/openBrowser?url=https://cloud.zirve-bilgisayar.com/eicrapro/takip-hazirla');
  }

  private async heartbeat() {
    this.signBusy = true;
    try {
      this.signRunning = await this.checkSignService();
    } finally {
      this.signBusy = false;
    }
  }

  private async refreshSignStatus() {
    try {
      localStorage.setItem('home.autoRefreshEnabled', String(this.autoRefreshEnabled));
    } catch (_) {
      /* ignore */
    }
    await this.heartbeat();
    if (this.autoRefreshEnabled) {
      this.nextRefreshIn = this.refreshIntervalSec;
    }
  }

  private async refreshSmartCards(showToast = false): Promise<void> {
    if (this.smartCardLoading) return;
    this.smartCardLoading = true;
    try {
      await this.availableCards({ silent: !showToast });
    } finally {
      this.smartCardLoading = false;
    }
  }

  private startAutoRefreshTimers() {
    this.stopAutoRefreshTimers();
    this.nextRefreshIn = this.refreshIntervalSec;
    this.countdownTimer = window.setInterval(async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (!this.autoRefreshEnabled) return;
      this.nextRefreshIn = Math.max(0, this.nextRefreshIn - 1);
      if (this.nextRefreshIn === 0) {
        try {
          await this.refreshSignStatus();
        } finally {
          this.nextRefreshIn = this.refreshIntervalSec;
        }
      }
    }, 1000) as unknown as number;
  }

  private stopAutoRefreshTimers() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer as any);
      this.countdownTimer = null;
    }
  }

  private handleVisibilityChange = () => {
    if (typeof document !== 'undefined' && document.hidden) {
      this.stopAutoRefreshTimers();
    } else if (this.autoRefreshEnabled) {
      this.startAutoRefreshTimers();
    }
  };

  private restorePreferences() {
    if (typeof localStorage === 'undefined') return;
    try {
      const savedAuto = localStorage.getItem('home.autoRefreshEnabled');
      if (savedAuto === 'true' || savedAuto === 'false') {
        this.autoRefreshEnabled = savedAuto === 'true';
      }
    } catch (_) {
      /* ignore */
    }
  }
}
